import {
  AloftArea,
  AloftPrice,
  CraftsmanAdjustmentProps,
  ORDER_FEATURES,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence, selectCompTypes } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';

const calculateSpaAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;

  if (compType === 'arv') {
    subjectImprovements = improvements.subjectArv;
  }
  const compImprovements = improvements[compID];

  const subjectPropertyIdentification = propertyIdentification.subject;
  const compPropertyIdentification = propertyIdentification[compID];

  if (
    isUndefined(subjectImprovements) ||
    isUndefined(compImprovements) ||
    isUndefined(depreciationRatios) ||
    isUndefined(subjectPropertyIdentification) ||
    isUndefined(compPropertyIdentification)
  ) {
    return undefined;
  }

  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) {
    return undefined;
  }

  const { spa } = subjectImprovements;
  const { spa: compSpa } = compImprovements;
  const {
    address: { PostalCode: zipCode },
  } = subjectPropertyIdentification;
  const {
    address: { PostalCode: compZipCode },
  } = compPropertyIdentification;

  if (isUndefined(zipCode) || isUndefined(compZipCode)) {
    return undefined;
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  return adjCalculator.getSpaAdj({
    subject: {
      hasFeature: ORDER_FEATURES.spa.hasFeature(subjectImprovements),
      sqft: spa?.area ? AloftArea.fromNumber(spa.area.unitAmount, spa.area.unit) : undefined,
      quality: spa?.quality,
      zipCode,
    },
    comp: {
      hasFeature: ORDER_FEATURES.spa.hasFeature(compImprovements),
      sqft: compSpa?.area
        ? AloftArea.fromNumber(compSpa.area.unitAmount, compSpa.area.unit)
        : undefined,
      quality: compSpa?.quality,
      zipCode: compZipCode,
    },
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

export const selectSpaAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'spa'),
    (state, id: string) => id,
    selectCompTypes,
  ],
  calculateSpaAdj,
);

export const selectSpaAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state) => selectDepreciationRatiosForFeature(state, 'spa'),
    selectCompSequence,
    selectCompTypes,
  ],
  (adjustmentVersion, improvements, propertyIdentification, depreciationRatios, compSequence, compTypes) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateSpaAdj(
        adjustmentVersion,
        improvements,
        propertyIdentification,
        depreciationRatios,
        propID,
        compTypes,
      );

      if (isDefined(adj)) acc[propID] = adj;

      return acc;
    }, {}),
);
